import { Link } from '@tanstack/react-router'
import { useSession } from '@/session/useSession'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import styles from './Sidebar.module.css'
import { SidebarAccount } from './SidebarAccount'
import { type NavGroup, SidebarGroup } from './SidebarGroup'
import { useCollapsedGroups } from './useCollapsedGroups'
import { useIsSidebarCollapsed } from './useSidebarCollapsed'

export type { NavGroup, NavLink } from './SidebarGroup'

export type SidebarProps = {
  groups: NavGroup[]

  home: string
  mark: string
  fallback: string
  onSignOut: () => void
  onNavigate?: () => void
}

export function Sidebar({ groups, home, mark, fallback, onSignOut, onNavigate }: SidebarProps) {
  const { organization, viewer } = useSession()
  const { isCollapsed, toggle } = useCollapsedGroups()
  const rail = useIsSidebarCollapsed()

  const shown = groups.filter((group) => group.links.length > 0)
  const name = organization ?? fallback

  const brand = (
    <Link
      to={home}
      className={styles.brand}
      data-collapsed={rail ? '' : undefined}
      aria-label={rail ? name : undefined}
      onClick={onNavigate}
    >
      <span className={styles.mark} aria-hidden="true">
        {mark}
      </span>
      {rail ? null : <span className={styles.name}>{name}</span>}
    </Link>
  )

  return (
    <>
      {rail ? (
        <Tooltip>
          <TooltipTrigger render={brand} />
          <TooltipContent side="right">{name}</TooltipContent>
        </Tooltip>
      ) : (
        brand
      )}

      <div className={styles.groups} data-collapsed={rail ? '' : undefined}>
        {shown.map((group) => (
          <SidebarGroup
            key={group.key}
            group={group}
            open={!isCollapsed(group.key)}
            onOpenChange={() => toggle(group.key)}
            onNavigate={onNavigate}
          />
        ))}
      </div>

      <SidebarAccount name={viewer?.name || viewer?.email || ''} onSignOut={onSignOut} />
    </>
  )
}
